import * as React from 'react'
import { FormikValues } from 'formik'

import rights from '@utils/RightManager'

import Select from '@components/select/select'
import MultiSelect from '@components/select/multiSelect'
import Calendar from '@components/calendar/calendar'
import Checkboxes from '@components/checkboxes/checkboxes'
import CustomSwitch from '@components/switch/Switch'

import styled from '@theme/styled-components'
import Theme from '@theme/Theme'

import useI18n from '@store/i18n/useI18n'

import apis from '@api/api'

interface Props {
  values: FormikValues
  changeAndSubmit: (fieldName: string, e: any) => void
  setScreenStatus: (s: ScreenStatus) => void
  mode: Mode
}

const ALL_FILTERS: FilterType[] = [
  //Filtres appliqués aux valeur des affaires
  'CAN_FILTER_RESPONSIBLE',
  'CAN_FILTER_COMPANY',
  'CAN_FILTER_NO_COMPANY_BUSINESSES',
  'CAN_FILTER_GROUP_COMPANY',
  //Filtres appliqués aux valeurs des déclarations
  'CAN_FILTER_STEP',
  'CAN_FILTER_DATE',
  //Checkboxes
  'CAN_FILTER_SOURCE',
  'CAN_FILTER_STATE',
  'CAN_FILTER_HAS_DECLARATIONS',
]

const HOME_FILTERS = ALL_FILTERS.filter((filter) => filter !== 'CAN_FILTER_NO_COMPANY_BUSINESSES')
const BOARD_FILTERS = ALL_FILTERS.filter(
  (filter) =>
    filter !== 'CAN_FILTER_GROUP_COMPANY' &&
    filter !== 'CAN_FILTER_STEP' &&
    filter !== 'CAN_FILTER_DATE' &&
    filter !== 'CAN_FILTER_HAS_DECLARATIONS'
)

const RegularFilters = ({ values, changeAndSubmit, setScreenStatus, mode }: Props) => {
  const i18n = useI18n()

  // Choix du responsable de l'affaire
  const [responsableOptions, setResponsableOptions] = React.useState<Option[]>([])
  // Choix de(s) l'étape(s) de l'affaire
  const [stepsOptions, setStepsOptions] = React.useState<Option[]>([])
  // Choix de l'entreprise de l'affaire
  const [companyOptions, setCompanyOptions] = React.useState<Option[]>([])
  // Choix du groupement de l'affaire
  const [companyGroupOptions, setCompanyGroupOptions] = React.useState<Option[]>([])

  React.useEffect(() => {
    Promise.all([apis.responsible.list(), apis.step.list(), apis.company.list(), apis.company.listCompanyGroups()])
      .then(([responsibles, steps, companies, companyGroups]) => {
        setResponsableOptions(responsibles.map((r) => ({ value: r.id, label: r.firstname + ' ' + r.lastname })))
        setStepsOptions(steps.sort((a, b) => a.index - b.index).map((s) => ({ value: s.id, label: s.wording })))
        setCompanyOptions(companies.map((c) => ({ value: c.id, label: c.name })))
        setCompanyGroupOptions(companyGroups.map((g) => ({ value: g.id, label: g.name })))
        setScreenStatus('fetched')
      })
      .catch(() => setScreenStatus('error'))
  }, [])

  const filtersToDisplay = (chosenMode: Mode) => {
    switch (chosenMode) {
      case 'home':
        return HOME_FILTERS
      case 'board':
        return BOARD_FILTERS
      default:
        return ALL_FILTERS
    }
  }

  const filterBusinessValueTitle = !!filtersToDisplay(mode).find(
    (filter) =>
      filter === 'CAN_FILTER_RESPONSIBLE' ||
      filter === 'CAN_FILTER_COMPANY' ||
      filter === 'CAN_FILTER_NO_COMPANY_BUSINESSES' ||
      filter === 'CAN_FILTER_GROUP_COMPANY'
  )
  const filterDeclarationValueTitle = !!filtersToDisplay(mode).find(
    (filter) => filter === 'CAN_FILTER_STEP' || filter === 'CAN_FILTER_DATE'
  )

  return (
    <ChoicesContainer>
      {(rights.canFilterOnResponsible() || rights.canFilterOnCompany()) && filterBusinessValueTitle && (
        <TitleContainer>
          <Title>{i18n.t('components.menu.filterTitle.business')}</Title>
        </TitleContainer>
      )}
      {rights.canFilterOnResponsible() && filtersToDisplay(mode).includes('CAN_FILTER_RESPONSIBLE') && (
        <Select
          options={responsableOptions}
          selected={values.responsable}
          setSelected={(e) => changeAndSubmit('responsable', e)}
          placeholder={i18n.t('components.menu.placeholder.responsable')}
          isClearable
        />
      )}
      {rights.canFilterOnCompany() && filtersToDisplay(mode).includes('CAN_FILTER_COMPANY') && (
        <Select
          options={companyOptions}
          selected={values.company}
          setSelected={(e) => changeAndSubmit('company', e)}
          placeholder={i18n.t('components.menu.placeholder.company')}
          isClearable
        />
      )}

      {rights.canFilterOnCompany() && filtersToDisplay(mode).includes('CAN_FILTER_NO_COMPANY_BUSINESSES') && (
        <SwitchContainer>
          <CustomSwitch
            value={values.noCompanyBusinesses}
            setValue={(e) => changeAndSubmit('noCompanyBusinesses', e)}
            label={i18n.t('components.monitorByRegion.withoutCompany')}
            labelFont={Theme.fonts.checkboxesTitle}
          />
        </SwitchContainer>
      )}

      {rights.canFilterOnCompanyGroup() && filtersToDisplay(mode).includes('CAN_FILTER_GROUP_COMPANY') && (
        <Select
          options={companyGroupOptions}
          selected={values.companyGroup}
          setSelected={(e) => changeAndSubmit('companyGroup', e)}
          placeholder={i18n.t('components.menu.placeholder.companyGroup')}
          isClearable
        />
      )}
      {filterDeclarationValueTitle && (
        <TitleContainer>
          <Title>{i18n.t('components.menu.filterTitle.declaration')}</Title>
        </TitleContainer>
      )}
      {filtersToDisplay(mode).includes('CAN_FILTER_STEP') && (
        <MultiSelect
          options={stepsOptions}
          selected={values.steps}
          setSelected={(e) => changeAndSubmit('steps', e)}
          placeholder={i18n.t('components.menu.placeholder.step')}
          isClearable
        />
      )}
      {filtersToDisplay(mode).includes('CAN_FILTER_DATE') && (
        <Calendar
          startDate={values.startDate ? new Date(values.startDate) : undefined}
          setStartDate={(e) => changeAndSubmit('startDate', e)}
          endDate={values.endDate ? new Date(values.endDate) : undefined}
          setEndDate={(e) => changeAndSubmit('endDate', e)}
          placeholder={i18n.t('components.menu.placeholder.date')}
        />
      )}
      <CheckboxesContainer>
        {rights.canFilterOnSource() && filtersToDisplay(mode).includes('CAN_FILTER_SOURCE') && (
          <Checkboxes
            title={i18n.t('components.menu.checkboxes.sources')}
            options={values.sources}
            setOptions={(e) => changeAndSubmit('sources', e)}
          />
        )}
        {filtersToDisplay(mode).includes('CAN_FILTER_STATE') && (
          <Checkboxes
            title={i18n.t('components.menu.checkboxes.statuses')}
            options={values.statuses}
            setOptions={(e) => changeAndSubmit('statuses', e)}
          />
        )}
        {rights.canFilterWithWithout() && filtersToDisplay(mode).includes('CAN_FILTER_HAS_DECLARATIONS') && (
          <Checkboxes
            title={i18n.t('components.menu.checkboxes.hasDeclarations')}
            options={values.hasDeclarations}
            setOptions={(e) => changeAndSubmit('hasDeclarations', e)}
          />
        )}
      </CheckboxesContainer>
    </ChoicesContainer>
  )
}
export default RegularFilters

const ChoicesContainer = styled('div')`
  display: flex;
  padding: 20px 0;
  padding-top: 10px;
  flex-direction: column;
  background: ${(props) => props.theme.colors.filterBackgroundColor};
  overflow: scroll;
`
const CheckboxesContainer = styled('div')`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
`
const TitleContainer = styled('div')`
  margin: 10px 20px;
`
const Title = styled('div')`
  ${(props) => props.theme.fonts.checkboxesTitle};
`
const SwitchContainer = styled('div')`
  padding: 0px 20px;
`
